import { useEffect, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { LoadingSkeleton } from "@a2z/ui";
import { ErrorState } from "@/shared/components/ErrorState";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useLocale } from "@seller/app/locale";
import { getSellerPath } from "@/lib/routing/portal-urls";
import { useSellerStoreSlug } from "@/lib/routing/useSellerStoreSlug";
import { usePageChrome } from "@/shared/layout/PageChromeContext";
import { PageTransition } from "@/shared/components/PageTransition";
import { useIsMobile } from "@/shared/hooks/useIsMobile";
import { useSellerTransactionsQuery } from "@/modules/transactions/services/useSellerTransactionsQuery";
import { TransactionsList } from "@/modules/transactions/components/TransactionsList";
import { TransactionsFilters } from "@/modules/transactions/components/TransactionsFilters";
import { TransactionDetailDrawer } from "@/modules/transactions/components/TransactionDetailDrawer";
import {
  TRANSACTION_FILTER_ALL,
  type TransactionFiltersState,
} from "@/modules/transactions/types/transactions.types";

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

const EMPTY_FILTERS: TransactionFiltersState = {
  status: TRANSACTION_FILTER_ALL,
  provider: TRANSACTION_FILTER_ALL,
  from: "",
  to: "",
  search: "",
};

function countActiveFilters(filters: TransactionFiltersState): number {
  let count = 0;
  if (filters.status !== TRANSACTION_FILTER_ALL) count += 1;
  if (filters.provider !== TRANSACTION_FILTER_ALL) count += 1;
  if (filters.from.length > 0 || filters.to.length > 0) count += 1;
  return count;
}

/**
 * Store-wide payment transactions. Until now a seller could only see the
 * transaction attached to a single order; this lists every gateway transaction
 * in the store with status/provider/date/search filters, and opens one in a
 * detail drawer.
 *
 * The open transaction is URL-driven (`?transaction=<id>`) so browser back
 * closes the drawer — the same pattern the customers and products pages use.
 */
export function TransactionsPage(): ReactElement {
  const locale = useLocale();
  const storeSlug = useSellerStoreSlug();
  const t = useTranslations();
  const isMobile = useIsMobile();
  const { setHero, setHeroAux } = usePageChrome();

  const [filters, setFilters] =
    useState<TransactionFiltersState>(EMPTY_FILTERS);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [offset, setOffset] = useState(0);

  const [searchParams, setSearchParams] = useSearchParams();
  const openTransactionId = searchParams.get("transaction");

  const openTransaction = (id: string): void => {
    const next = new URLSearchParams(searchParams);
    next.set("transaction", id);
    setSearchParams(next);
  };
  const closeTransaction = (): void => {
    const next = new URLSearchParams(searchParams);
    next.delete("transaction");
    setSearchParams(next);
  };

  // This page renders its own title inline, so clear the shared chrome.
  useEffect(() => {
    setHero(null);
    setHeroAux(null);
    return () => {
      setHero(null);
      setHeroAux(null);
    };
  }, [setHero, setHeroAux]);

  useEffect(() => {
    const id = setTimeout(
      () => setDebouncedSearch(filters.search),
      SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(id);
  }, [filters.search]);

  const { data, isLoading, error, refetch } = useSellerTransactionsQuery({
    storeSlug,
    status:
      filters.status === TRANSACTION_FILTER_ALL ? undefined : filters.status,
    provider:
      filters.provider === TRANSACTION_FILTER_ALL
        ? undefined
        : filters.provider,
    from: filters.from || undefined,
    to: filters.to || undefined,
    search: debouncedSearch.trim() || undefined,
    limit: PAGE_SIZE,
    offset,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const shownFrom = total === 0 ? 0 : offset + 1;
  const shownTo = Math.min(offset + items.length, total);
  const activeFilterCount = countActiveFilters(filters);
  const hasAnyFilter =
    activeFilterCount > 0 || debouncedSearch.trim().length > 0;

  const changeFilters = (next: TransactionFiltersState): void => {
    setFilters(next);
    setOffset(0);
  };
  const resetFilters = (): void => {
    setFilters(EMPTY_FILTERS);
    setOffset(0);
  };

  const orderHref = (orderId: string): string =>
    `${getSellerPath(locale, storeSlug, "/orders")}?order=${encodeURIComponent(orderId)}`;

  let content: ReactNode;
  if (isLoading && !data) {
    content = <LoadingSkeleton variant="list" rows={6} />;
  } else if (error) {
    content = (
      <ErrorState message={t(keys.transactions.loadError)} onRetry={refetch} />
    );
  } else if (items.length === 0) {
    // Two empty states: nothing matched the filters (recoverable) vs. the store
    // has no transactions at all (explain why, say what unlocks it).
    content = (
      <div className="flex flex-col items-center gap-2 rounded-[18px] border border-[color:var(--line2)] bg-[color:var(--card)] px-6 py-14 text-center">
        <p className="text-[15px] font-semibold text-[color:var(--text-primary)]">
          {t(
            hasAnyFilter
              ? keys.transactions.emptyFilteredTitle
              : keys.transactions.emptyTitle,
          )}
        </p>
        <p className="text-[13px] text-[color:var(--text-muted)]">
          {t(
            hasAnyFilter
              ? keys.transactions.emptyFilteredBody
              : keys.transactions.emptyBody,
          )}
        </p>
        {hasAnyFilter ? (
          <button
            type="button"
            onClick={resetFilters}
            className="mt-2 inline-flex h-9 items-center justify-center rounded-[9px] border border-[color:var(--border-strong)] bg-[color:var(--soft2)] px-3.5 text-[12.5px] font-semibold text-[color:var(--text-primary)] transition-colors hover:bg-[color:var(--hover)]"
          >
            {t(keys.common.reset)}
          </button>
        ) : null}
      </div>
    );
  } else {
    content = (
      <TransactionsList
        items={items}
        locale={locale}
        orderHref={orderHref}
        onOpenTransaction={openTransaction}
      />
    );
  }

  const showFooter = !isLoading && !error && items.length > 0;

  return (
    <>
      <PageTransition className="w-full">
        <div
          className={[
            "flex w-full flex-col gap-4",
            isMobile ? "px-4 pb-24 pt-4" : "px-7 py-6",
          ].join(" ")}
        >
          <div className="min-w-0">
            <h1
              className={[
                "font-bold tracking-[-0.01em] text-[color:var(--text-primary)]",
                isMobile
                  ? "text-[26px] leading-tight"
                  : "text-[24px] leading-tight",
              ].join(" ")}
            >
              {t(keys.transactions.pageTitle)}
            </h1>
            <p className="mt-1 text-[13px] text-[color:var(--text-muted)]">
              {t(keys.transactions.pageSubtitle)}
            </p>
          </div>

          <TransactionsFilters
            value={filters}
            onChange={changeFilters}
            onReset={resetFilters}
            activeCount={activeFilterCount}
          />

          {content}

          {showFooter ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span className="text-[12.5px] text-[color:var(--text-muted)]">
                {t(keys.transactions.showingCount, {
                  shown: `${shownFrom}–${shownTo}`,
                  total,
                })}
              </span>
              {totalPages > 1 ? (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setOffset((prev) => Math.max(0, prev - PAGE_SIZE))
                    }
                    disabled={offset === 0}
                    className="inline-flex h-9 items-center justify-center rounded-[9px] border border-[color:var(--border-light)] bg-[color:var(--surface)] px-3 text-[12.5px] font-semibold text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--hover)] disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {t(keys.common.previous)}
                  </button>
                  <span className="text-[12.5px] text-[color:var(--text-muted)]">
                    <bdi>
                      {Math.floor(offset / PAGE_SIZE) + 1} / {totalPages}
                    </bdi>
                  </span>
                  <button
                    type="button"
                    onClick={() => setOffset((prev) => prev + PAGE_SIZE)}
                    disabled={offset + PAGE_SIZE >= total}
                    className="inline-flex h-9 items-center justify-center rounded-[9px] border border-[color:var(--border-light)] bg-[color:var(--surface)] px-3 text-[12.5px] font-semibold text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--hover)] disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {t(keys.common.next)}
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </PageTransition>

      <TransactionDetailDrawer
        storeSlug={storeSlug}
        transactionId={openTransactionId}
        locale={locale}
        orderHref={orderHref}
        onClose={closeTransaction}
      />
    </>
  );
}
